import React from 'react';
import LMSHeader from '../components/LMSHeader';

const PrivacyPolicyPage = () => {
  return (
    <>
      <LMSHeader />
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
              Last updated: {new Date("2025-06-02").toLocaleDateString()}
            </p>

            <div className="text-neutral-900 dark:text-neutral-400 max-w-none space-y-6">
              <section>
                <h2><strong>1. Introduction</strong></h2>
                <p>
                  Boleum Energy & Technology Limited ("Boleum," "we," "us," or "our") is committed to
                  protecting your privacy. This Privacy Policy outlines how we collect, use, disclose, and safeguard your personal information when you access or use our online learning and
                  training platform, including all associated websites, mobile applications, and services
                  (collectively, the "Platform").
                </p>

                <p className='mt-3'>
                  By using our Platform, you agree to the terms of this Privacy Policy.
                </p>
              </section>

              <section>
                <h2><strong>2. Information We Collect</strong></h2>
                <p>
                  <strong>2.1 We may collect the following personal data when you register or interact with our Platform:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Full name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Date of birth</li>
                    <li>Gender</li>
                    <li>Country, state, city of residence</li>
                    <li>Profile photo</li>
                    <li>Educational background</li>
                    <li>Employment details (for professional learners)</li>
                    <li>Payment and billing information</li>
                  </ul>
                <p>
                  <strong>2.2 Academic & Training Data:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Course enrollments and completions</li>
                    <li>Quiz scores and assignment submissions</li>
                    <li>Certification records</li>
                    <li>Learning preferences and feedback</li>
                    <li>Attendance logs for live sessions</li>
                    <li>Uploaded documents and training materials</li>
                  </ul>
                <p>
                  <strong>2.3 Technical & Usage Data:</strong>
                </p>
                 <ul className="list-disc pl-6 space-y-1">
                    <li>IP address</li>
                    <li>Device type and operating system</li>
                    <li>Browser type and version</li>
                    <li>Access time and session duration</li>
                    <li>Clickstream data and navigation patterns</li>
                    <li>Video watch time and interaction logs</li>
                  </ul>
                <p>
                  <strong>2.4 Instructor & Facilitator Data:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1 ">
                    <li>Professional title and qualifications</li>
                    <li>Curriculum Vitae (CV)</li>
                    <li>Government-issued ID</li>
                    <li>Bank account details for payouts</li>
                    <li>Teaching history and feedback ratings</li>
                  </ul>
              </section>

              <section>
                <h2><strong>3. How We Use Your Information</strong></h2>
                <p>We use your information to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Deliver and manage educational content and training programs</li>
                  <li>Authenticate user identity and manage accounts</li>
                  <li>Process payments and issue certificates</li>
                  <li>Track progress and personalize learning paths</li>
                  <li>Communicate updates, reminders, and support messages</li>
                  <li>Analyze usage trends to improve platform performance</li>
                  <li>Facilitate instructor-learner interactions</li>
                  <li>Comply with legal and regulatory obligations</li>
                </ul>
              </section>

              <section>
                <h2><strong>4. Sharing of Information</strong></h2>
                <p>
                  <strong>4.1 With Instructors and Facilitators:</strong> We may share your name, email, and course activity with instructors to support learning outcomes and communication.
                </p>
                <p>
                  <strong>4.2 With Service Providers:</strong> We engage trusted third parties to support our operations, including.
                </p>
                 <ul className="list-disc pl-6 space-y-1">
                    <li>Payment processors (e.g., PayStack)</li>
                    <li>Cloud hosting and storage providers</li>
                    <li>Email and notification services</li>
                    <li>Learning analytics and reporting tools</li>
                  </ul>
                <p>
                  <strong>4.3 With Employers or Sponsors:</strong> If your training is sponsored by an employer or third party, we may share progress reports and certification status with them, subject to your consent.
                </p>
                <p>
                  <strong>4.4 Legal and Regulatory Disclosure:</strong> We may disclose your information when required by law, court order, or government
                  regulation, or to protect our rights and users' safety.
                </p>
                <p>
                  <strong>4.5 No Sale of Personal Data:</strong> We do not sell your personal information to third parties.
                </p>
              </section>

              <section>
                <h2><strong>5. Data Security</strong></h2>
                <p>
                  We implement robust security measures to protect your data, including:
                </p>
                 <ul className="list-disc pl-6 space-y-1">
                    <li>SSL encryption for data transmission</li>
                    <li>Secure payment gateways</li>
                    <li>Role-based access controls</li>
                    <li>Regular vulnerability assessments and audits</li>
                  </ul>
                <p>
                  However, no method of transmission over the internet is completely secure. We encourageusers to protect their login credentials and report any suspicious activity.
                </p>
              </section>

              <section>
                <h2><strong>6. Data Retention</strong></h2>
                <p>
                  We retain your personal data for as long as your account is active or necessary to provideservices. After account deletion:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Data is retained for 12 months for legal and audit purposes</li>
                    <li>Certification records may be retained indefinitely for verification</li>
                    <li>Data is anonymized or securely deleted thereafter</li>
                  </ul>
              </section>

              <section>
                <h2><strong>7. Your Rights</strong></h2>
                <p>Depending on your jurisdiction, you may have the right to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Access, correct, or delete your personal data</li>
                  <li>Restrict or object to certain processing activities</li>
                  <li>Request data portability</li>
                  <li>Withdraw consent where applicable</li>
                </ul>
                <p>To exercise these rights, contact us at: training@boleum.com</p>
              </section>

              <section>
                <h2><strong>8. Children's Privacy</strong></h2>
                <p>
                  Our Platform is not intended for individuals under the age of 16. We do not knowingly
                  collect personal data from children under 16. If we discover such data has been collected, we will delete it promptly.
                </p>
              </section>

              <section>
                <h2><strong>9. International Data Transfers</strong></h2>
                <p>
                  Your data may be stored or processed in countries outside Nigeria. We ensure appropriate safeguards are in place, such as:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Standard contractual clauses</li>
                    <li>Data processing agreements</li>
                    <li>Secure cloud infrastructure</li>
                  </ul>
              </section>

              <section>
                <h2><strong>10. Use of Cookies and Tracking Technologies</strong></h2>
                <p>
                  We use cookies and similar technologies to:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Remember user preferences</li>
                    <li>Analyze traffic and usage patterns</li>
                    <li>Deliver personalized content</li>
                  </ul>
                <p>You may manage cookie preferences through your browser settings.</p>
              </section>

              <section>
                <h2><strong>11. Third-Party Links</strong></h2>
                <p>
                  Our Platform may contain links to third-party websites or services. We are not responsible
                  for the privacy practices of those entities. We encourage users to review their privacy
                  policies.
                </p>
              </section>

              <section>
                <h2><strong>12. Changes to This Policy</strong></h2>
                <p>
                  We may update this Privacy Policy periodically. Material changes will be communicated via
                  email or platform notifications. Continued use of the Platform after changes indicates
                  acceptance.
                </p>
                <p>
                  <strong>Email:</strong> training@boleum.com<br />
                  <strong>Address:</strong> #1 Beside Christ Missionaries Crusaders Church, Opp NNPC Housing Estate Ekpan, Delta State, Nigeria
                </p>
              </section>

              <section>
                <h2><strong>13. Contact Us</strong></h2>
                <p>
                  For privacy-related inquiries or to exercise your rights, please contact our Data Protection
                  Officer:
                </p>
                <p className='mt-2'>
                  Boleum Energy & Technology Limited
                </p>
                <p>
                  <strong>Email:</strong> training@boleum.com
                </p>
                <p>
                 <strong>Address:</strong> 1 Beside Christ Missionaries Crusaders Church, Opp NNPC Housing Estate
                  Ekpan, Delta State, Nigeria.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;